import type { LocalDate } from '../dates';
import { inRange, type DateRange } from '../ledger/counting';
import { eachDate } from './dayMath';
import type { SmokeLogLike } from './types';

export interface DayTally {
  date: LocalDate;
  count: number;
  costMinor: number;
  /** A written total decides this day (taps before it don't count). */
  hasTotal: boolean;
  /** Latest tap that counts, if any. */
  lastTapAt: string | null;
}

function byAt(a: SmokeLogLike, b: SmokeLogLike): number {
  if (a.at !== b.at) return a.at < b.at ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/**
 * Spec §7: a day's total is the latest day_total for that day, if one exists, plus taps logged
 * after it; otherwise it is the number of taps. A tap at the same instant as the total is treated
 * as before it, so nothing is ever counted twice.
 */
export function tallyDay(logs: readonly SmokeLogLike[], date: LocalDate): DayTally {
  const live = logs.filter((l) => l.deletedAt === null && l.date === date);
  const latest =
    live
      .filter((l) => l.kind === 'day_total')
      .sort(byAt)
      .at(-1) ?? null;
  const taps = live
    .filter((l) => l.kind === 'tap' && (latest === null || l.at > latest.at))
    .sort(byAt);
  return {
    date,
    count: (latest?.count ?? 0) + taps.reduce((s, l) => s + l.count, 0),
    costMinor: (latest?.costMinor ?? 0) + taps.reduce((s, l) => s + l.costMinor, 0),
    hasTotal: latest !== null,
    lastTapAt: taps.at(-1)?.at ?? null,
  };
}

/** One tally per day in the range (inclusive), empty days included. */
export function tallyRange(logs: readonly SmokeLogLike[], range: DateRange): DayTally[] {
  const byDate = new Map<LocalDate, SmokeLogLike[]>();
  for (const l of logs) {
    if (!inRange(l.date, range)) continue;
    const list = byDate.get(l.date) ?? [];
    list.push(l);
    byDate.set(l.date, list);
  }
  return eachDate(range.start, range.end).map((d) => tallyDay(byDate.get(d) ?? [], d));
}
